import { skipToken, useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Logo } from "@/components/Logo";
import { AuthLayout } from "../../components/catalyst/AuthLayout";
import { orpc } from "../../orpc/client";
import { ErrorState, LoadingState, SuccessState } from "./components";

type Status = "idle" | "loading" | "error" | "success";

/**
 * Partner signup page - handles Stripe Connect onboarding.
 * Terms acceptance is handled by PartnerTermsGate at the route level.
 */
export function PartnerSignup() {
	const [status, setStatus] = useState<Status>("idle");
	const [errorMsg, setErrorMsg] = useState("");

	const searchParams = useSearch({ from: "/partner" });
	const token = searchParams.token;
	const success = searchParams.success;
	const stripeSuccess = searchParams.stripe_success;

	const { data, isLoading, error } = useQuery(
		orpc.stripePartnerConnection.signup.queryOptions({
			input:
				!!token && !success && !stripeSuccess
					? { token: token ?? "" }
					: skipToken,
			retry: 1,
		}),
	);

	useEffect(() => {
		if (success || stripeSuccess) {
			setStatus("success");
			return;
		}

		if (isLoading) {
			setStatus("loading");
		} else if (error) {
			setStatus("error");
			setErrorMsg(error.message || "Invalid or expired link");
		} else if (data?.redirect) {
			// Terms already accepted (handled by gate), redirect to Stripe
			window.location.href = data.redirect;
		} else if (!token) {
			setStatus("error");
			setErrorMsg("Invalid link. Please check your email.");
		}
	}, [success, stripeSuccess, isLoading, error, data, token]);

	function renderContent() {
		if (status === "idle" || status === "loading") {
			return <LoadingState />;
		}
		if (status === "success") {
			return <SuccessState />;
		}
		if (status === "error") {
			return <ErrorState message={errorMsg} />;
		}
		return null;
	}

	return (
		<AuthLayout>
			<div className="flex flex-col gap-6 w-full max-w-2xl text-center">
				<div className="flex flex-col items-center gap-4">
					<Logo />
					{renderContent()}
				</div>
			</div>
		</AuthLayout>
	);
}
